<template>
  <div>
    <Sections grid="H" padding-y="py-6 md:py-10">
      <template #content>
        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-12">
            <Breadcrumbs
              v-if="breadcrumbs"
              :data="t('experimentalCenters.projects.projectID')"
              :path="breadcrumbs.path"
              :section="breadcrumbs.section"
            />
          </div>
          <div
            class="col-span-12 lg:col-span-8"
            :class="projectData?.icons && projectData?.icons.pt_icons ? 'lg:col-start-2 ' : ''"
          >
            <SummaryMainContent
              class="flex flex-col mt-11 mb-6 lg:mb-12"
              :title="projectData?.title ? projectData?.title : projectData?.meta_title"
              :summary="projectData?.summary"
            >
              <template #labels>
                <span
                  v-if="projectData?.start_date && projectData?.finish_date"
                  class="self-start inline px-1 rounded-sm font-semibold font-sans text-sm bg-gray-soft text-gray"
                  v-text="
                    `${t('experimentalCenters.projects.date.start')}: ${dateToString(
                      projectData?.start_date
                    )} - ${
                      projectData?.finish_date
                        ? `${t('experimentalCenters.projects.date.end')}: ${dateToString(
                            projectData?.finish_date
                          )}`
                        : t('experimentalCenters.projects.date.continue')
                    }`
                  "
                />
                <Labels
                  v-if="projectData?.tags?.length"
                  class="mb-6"
                  label-type="transparent"
                  :items="projectData?.tags"
                  to-search="articles"
                />
              </template>
            </SummaryMainContent>

            <div class="flex flex-wrap lg:hidden mt-6 items-center w-full gap-6 mb-6 lg:mb-0">
              <Share
                v-if="shareNetworks && shareNetworks?.length"
                class="w-5/12 flex justify-center flex-auto bg-white-dark"
                :share-networks="shareNetworks"
                :url="urlShare"
                :title="projectData?.title ? projectData?.title : projectData?.meta_title"
                :description="projectData?.summary"
              />
              <Bookmark
                button="block"
                class="w-5/12 flex-auto"
                entity-name="project"
                :entity-id="projectData?.id"
                :favorite-entities="userFavorites"
              />
              <SummaryDownloadBook
                v-if="projectData?.mediateca && projectData?.mediateca?.length"
                class="w-full max-w-xs mx-auto"
                :image="projectData?.mediateca[0].cover_image"
                :text="projectData?.mediateca[0].title"
                :filename="
                  projectData?.mediateca[0].path
                    ? projectData?.mediateca[0].path
                    : projectData?.mediateca[0].filename
                "
              />
            </div>
          </div>
          <div v-if="projectData?.logo" class="hidden lg:block col-start-11 col-span-2 mt-11">
            <span class="block font-ubuntu text-sm font-semibold mb-4">
              {{ t('experimentalCenters.projects.logo') }}
            </span>
            <NuxtImg
              :key="projectData?.logo.url"
              :src="projectData?.logo.url"
              :alt="projectData?.logo.alternativeText"
              loading="lazy"
            />
          </div>
        </div>
      </template>
      <template #content-left>
        <div class="grid grid-cols-10 gap-4">
          <div v-if="projectData?.icons" class="col-span-12 lg:col-span-1 hidden lg:flex text-6xl">
            <ul v-if="projectData?.icons && projectData?.icons.pt_icons">
              <li v-for="icon in projectData?.icons.pt_icons" :key="icon.id" class="mb-4">
                <icon :name="icon.slug" :title="icon.label" class="stamp" />
              </li>
            </ul>
          </div>
          <div class="col-span-12 lg:col-span-8">
            <div class="pt-main-wrapper mb-6">
              <Gallery
                v-if="projectData?.gallery && projectData?.gallery?.length"
                class="mb-4"
                :images="projectData?.gallery"
              />
              <a
                v-if="projectData?.video_relation && projectData?.video_relation.video"
                href="#"
                @click.stop.prevent="playVideo(projectData?.video_relation.video.url)"
              >
                <NuxtImg
                  :key="projectData?.video_relation.video.media.url"
                  :alt="projectData?.video_relation.video.media.alternativeText"
                  :src="projectData?.video_relation.video.media.url"
                  class="pt-main-image"
                  loading="lazy"
                />
              </a>
              <NuxtImg
                v-else-if="
                  projectData?.main_image &&
                  projectData?.main_image.url &&
                  (!projectData?.gallery || projectData?.gallery?.length <= 0)
                "
                :key="projectData?.id"
                :src="projectData?.main_image.url"
                :alt="
                  projectData?.main_image.alternativeText
                    ? projectData?.main_image.alternativeText
                    : 'proyecto'
                "
                class="pt-main-image"
                loading="lazy"
              />
            </div>
            <div class="flex flex-col space-y-8 mb-6">
              <h2 class="font-ubuntu font-semibold text-2xl">
                {{ t('experimentalCenters.projects.centerAndTeam') }}
              </h2>
              <div class="grid grid-cols-12 gap-4">
                <nuxt-link
                  v-for="center in projectData?.centers"
                  :key="center.name"
                  class="text-sm text-blue col-span-12 lg:col-span-6 underline"
                  :to="`${FORM_VISIT_OUR_EXPERIMENTAL_CENTERS_BASE_URL}/${center.slug}`"
                >
                  {{ center.name }}
                </nuxt-link>
              </div>
            </div>
            <div v-if="projectData?.attorney">
              <p class="font-ubuntu font-semibold text-sm">
                {{ t('experimentalCenters.projects.responsible') }}
              </p>
              <Authors url-sub-domain="autor" :list="projectData?.attorney" />
              <hr class="mt-8 mb-6 md:mb-10 text-gray-soft" />
            </div>

            <div v-if="projectData?.team && projectData?.team?.length">
              <p class="font-ubuntu font-semibold text-sm">
                {{ t('experimentalCenters.projects.cajamarTeam') }}
              </p>
              <Authors url-sub-domain="autor" :list="projectData?.team" />
              <hr class="mt-8 mb-6 md:mb-10 text-gray-soft" />
            </div>

            <div class="mt-5 mb-12">
              <h2 class="font-ubuntu font-semibold text-2xl mb-6">
                {{ t('experimentalCenters.projects.scopeAndReach') }}
              </h2>
              <ul class="space-y-4">
                <li v-if="projectData?.scope" class="flex flex-wrap space-x-3 items-center">
                  <icon name="pt-university" class="text-sm" />
                  <span>{{ t('experimentalCenters.projects.scope') }}:</span>
                  <span class="font-semibold">{{
                    t(`experimentalCenters.projects.scopeList.${projectData?.scope}`)
                  }}</span>
                </li>
                <li v-if="projectData?.financing" class="flex flex-wrap space-x-3 items-center">
                  <icon name="pt-coins" class="text-sm" />
                  <span>{{ t('experimentalCenters.projects.financing') }}:</span>
                  <span class="font-semibold">{{
                    t(`experimentalCenters.projects.financingList.${projectData?.financing}`)
                  }}</span>
                </li>
                <li v-if="projectData?.work_line" class="flex flex-wrap space-x-3 items-center">
                  <icon name="pt-book-open" class="text-sm" />
                  <span>{{ t('experimentalCenters.projects.workLine') }}:</span>
                  <span class="font-semibold">{{
                    t(`experimentalCenters.projects.workLineList.${projectData?.work_line}`)
                  }}</span>
                </li>
                <li
                  v-if="projectData?.taxonomy?.length"
                  class="flex flex-wrap space-x-3 items-center"
                >
                  <icon name="pt-tag-alt" class="text-sm" />
                  <span>{{ t('experimentalCenters.projects.taxonomy') }}:</span>
                  <span
                    v-for="(taxonomy, index) in projectData?.taxonomy"
                    :key="taxonomy.value"
                    class="font-semibold"
                  >
                    <span class="flex">
                      {{ t(`experimentalCenters.projects.taxonomyList.${taxonomy.value}`) }}
                      <span v-if="index + 1 !== projectData?.taxonomy?.length">,</span>
                    </span>
                  </span>
                </li>
              </ul>
            </div>
            <div v-if="projectData?.content" class="mt-5 mb-12">
              <h2 class="font-ubuntu font-semibold text-2xl">
                {{ t('experimentalCenters.projects.content') }}
              </h2>
              <markdown v-if="projectData?.content" :content="projectData?.content" />
            </div>
            <div class="space-y-16">
              <div v-if="coordinatorsLogos?.length">
                <p class="font-semibold font-ubuntu mb-6">
                  {{ t('experimentalCenters.projects.coordinator') }}:
                </p>
                <div
                  class="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center"
                >
                  <NuxtImg
                    v-for="item in coordinatorsLogos"
                    :key="item.url"
                    :src="item.url"
                    :alt="item.alternativeText"
                    loading="lazy"
                    class="w-full"
                  />
                </div>
              </div>
              <div v-if="membersLogos?.length">
                <p class="font-semibold font-ubuntu mb-6">
                  {{ t('experimentalCenters.projects.members') }}:
                </p>
                <div
                  class="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center"
                >
                  <NuxtImg
                    v-for="item in membersLogos"
                    :key="item.url"
                    :src="item.url"
                    :alt="item.alternativeText"
                    loading="lazy"
                    class="w-full"
                  />
                </div>
              </div>
              <div v-if="participantsLogos?.length">
                <p class="font-semibold font-ubuntu mb-6">
                  {{ t('experimentalCenters.projects.participants') }}:
                </p>
                <div
                  class="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center"
                >
                  <NuxtImg
                    v-for="item in participantsLogos"
                    :key="item.url"
                    :src="item.url"
                    :alt="item.alternativeText"
                    loading="lazy"
                    class="w-full"
                  />
                </div>
              </div>
              <div v-if="collaboratorsLogos?.length">
                <p class="font-semibold font-ubuntu mb-6">
                  {{ t('experimentalCenters.projects.collaborators') }}:
                </p>
                <div
                  class="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center"
                >
                  <NuxtImg
                    v-for="item in collaboratorsLogos"
                    :key="item.url"
                    :src="item.url"
                    :alt="item.alternativeText"
                    loading="lazy"
                    class="w-full"
                  />
                </div>
              </div>
              <div v-if="sponsorsLogos?.length">
                <p class="font-semibold font-ubuntu mb-6">
                  {{ t('experimentalCenters.projects.sponsors') }}:
                </p>
                <div
                  class="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8 items-center justify-items-center"
                >
                  <NuxtImg
                    v-for="item in sponsorsLogos"
                    :key="item.url"
                    :src="item.url"
                    :alt="item.alternativeText"
                    loading="lazy"
                    class="w-full"
                  />
                </div>
              </div>
            </div>
            <ClientOnly>
              <comment-embed :id="projectData?.slug" class="mt-6" />
            </ClientOnly>
          </div>
        </div>
      </template>
      <template #content-right>
        <div class="hidden lg:block">
          <Share
            v-if="shareNetworks && shareNetworks?.length"
            :share-networks="shareNetworks"
            :url="urlShare"
            :title="projectData?.title ? projectData?.title : projectData?.meta_title"
            :description="projectData?.summary"
          />
          <div class="mt-4">
            <Bookmark
              button="block"
              entity-name="project"
              :entity-id="projectData?.id"
              :favorite-entities="userFavorites"
            />
          </div>
          <hr
            v-if="projectData?.mediateca && projectData?.mediateca?.length"
            class="hidden my-8 lg:block text-gray-very-light"
          />
          <SummaryDownloadBook
            v-if="projectData?.file && projectData?.file?.length"
            class="hidden lg:block"
            :image="projectData?.file[0].cover_image"
            :text="projectData?.file[0].title"
            :filename="
              projectData?.file[0].path ? projectData?.file[0].path : projectData?.file[0].filename
            "
          />
          <hr
            v-if="projectData?.file && projectData?.file?.length"
            class="hidden my-8 lg:block text-gray-very-light"
          />
        </div>
      </template>
    </Sections>
    <VideoLibraryPlayer ref="videoPlayer" />
  </div>
</template>

<script setup>
import { isEmptyObject } from '@/utils/helpers/object';
import { dateToString } from '@/utils/helpers/date';
import {
  VISIT_OUR_EXPERIMENTAL_CENTERS_BASE_URL,
  FORM_VISIT_OUR_EXPERIMENTAL_CENTERS_BASE_URL,
  STRUCTURED_DATA_TYPE
} from '~/utils/enums';

const { userFavorites } = useFavoriteEntities();
const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { createHeadSeo } = useHeadSeo();
const videoPlayer = ref(null);
const state = reactive({
  breadcrumbs: {
    path: VISIT_OUR_EXPERIMENTAL_CENTERS_BASE_URL.replace('/', ''),
    section: t(`breadcrumbs.centros`)
  },
  urlShare: process.env.APP_BASE_DOMAIN + route.fullPath
});
const { breadcrumbs, urlShare } = toRefs(state);
const { data } = await useAsyncData('project', async () => {
  const projectData = await store.dispatch('strapi/getProjectsBySlug', {
    slug: route.params.slug
  });
  return {
    projectData
  };
});

if (isEmptyObject(data?.value?.projectData)) {
  throw createError({ statusCode: 404 });
}

const shareNetworks = computed(() => store.getters['strapi/getByField']('common.social_sharing'));
const projectData = computed(() => data.value.projectData);
const coordinatorsLogos = computed(() =>
  projectData.value?.coordinators?.filter(e => e?.logo != null).map(e => e.logo)
);
const participantsLogos = computed(() =>
  projectData.value?.participants?.filter(e => e?.logo != null).map(e => e.logo)
);
const membersLogos = computed(() =>
  projectData.value?.members?.filter(e => e?.logo != null).map(e => e.logo)
);
const collaboratorsLogos = computed(() =>
  projectData.value?.collaborators?.filter(e => e?.logo != null).map(e => e.logo)
);
const sponsorsLogos = computed(() =>
  projectData.value?.sponsors?.filter(e => e?.logo != null).map(e => e.logo)
);
const playVideo = url => {
  videoPlayer.value.openPlayer(url);
};

watch(
  projectData,
  val => {
    const { meta_title: mainTitle, abstract, main_image: mainImage, og_image: ogImage } = val;
    const imgUrl = ogImage ? ogImage?.url : mainImage?.url;
    createHeadSeo(mainTitle, abstract, imgUrl, val, STRUCTURED_DATA_TYPE.ARTICLE);
  },
  { immediate: true, deep: true }
);
</script>

<style lang="scss" scoped>
hr {
  @apply text-gray-very-light;
}
.pt-labels {
  @apply mt-4;
}
.pt-main-content {
  .pt-labels {
    @apply mt-6 mb-4 lg:my-6;
  }
}

.pt-main-wrapper {
  @supports not (aspect-ratio: 16 / 9) {
    @apply aspect-w-16 aspect-h-9;
  }
  img {
    @apply w-full h-full object-cover rounded;
    @supports (aspect-ratio: 16 / 9) {
      aspect-ratio: 16 / 9;
    }
  }
}

.pt-notification-reports {
  @apply flex items-center bg-blue-very-light rounded my-4 p-4;
  width: fit-content;
}

.pt-published {
  @apply bg-gray-very-light text-sm text-gray px-1 py-px font-sans font-semibold rounded;
}

.pt-icons {
  &__vertical {
    @apply col-span-12 lg:col-span-1 hidden lg:flex;
  }
  &__horizontal {
    @apply flex flex-row lg:hidden;
  }
}

.stamp {
  @apply text-6xl;
}
</style>
